import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import useToggle from "../../hooks/useToggle";
import OperationTypes from "../../types";
import FormInput from "../common/FormInput";
import { nameof } from "../common/ObjectUtils";
import Dialogs from "./dialogs";
import VehicleEntranceService from "./service/VehicleEntranceService";
import { IVehicleFirstFueling } from "./types";
import { AutoCompleteOption } from "../common/AutoComplete";
import { onChangeTextNumber } from "../../utils";
import EnumChangeName from "../common/EnumChangeName";

const VehicleEntranceFirstFueling = (props: any) => {
  const { globalData } = useContext(GlobalContext);
  const { tenant, setLoading, userLocations } = useContext(ContextOperation);

  const [openNewAggiungi, toggleNewAggiungi] = useToggle();
  const [openErrorLocation, toggleErrorLocation] = useToggle();
  const [openConfirmStatus, toggleConfirmStatus] = useToggle();

  const t = useOperationTranslation();

  props.changeSubtitle(t("vehicleEntrance.subtitles.firstFueling"));

  const { i18n } = useTranslation();
  const snackbar = useSnackbar();

  const { plate } = useParams();

  const form = useForm<IVehicleFirstFueling>();

  const [vehicleData, setVehicleData] = useState<OperationTypes.Vehicle>();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  
  const { locationType } = EnumChangeName();

  useEffect(() => {
    if (plate) {
      VehicleEntranceService.firstFueling.get
        .vehicleByPlate(tenant, plate)
        .then((response) => {
          setVehicleData(response);

          form.reset({
            firstFuelLocationId: response.physicalLocationId,
          });
        });
    }
  }, []);

  const onSubmit = (data: IVehicleFirstFueling) => {
    if (engineType !== "electric" && uploadedFiles.length === 0) {
      return;
    }

    let dataObject = {
      plate: plate,
      firstFuelLocationId: data.firstFuelLocationId,
      firstFuelLocationType: vehicleData?.physicalLocationType,
      firstFuelDatetime: data.firstFuelDatetime,
      ...((engineType === "all" || engineType === "termic") &&
        data.firstFuelTermicLiters !== undefined && {
          firstFuelTermicLiters: parseFloat(
            data.firstFuelTermicLiters?.toString()?.replace(",", ".")
          ).toFixed(2),
        }),

      ...((engineType === "all" || engineType === "termic") &&
        data.firstFuelTermicCostPerLiter !== undefined && {
          firstFuelTermicCostPerLiter: parseFloat(
            data.firstFuelTermicCostPerLiter?.toString()?.replace(",", ".")
          ).toFixed(2),
        }),
      ...((engineType === "all" || engineType === "termic") && {
        currentTermicTankFilling: parseFloat(
          data.currentTermicTankFilling?.toString() || ""
        ).toFixed(0),
        firstFuelCardNumber: data.firstFuelCardNumber,
        firstFuelTotalCostTermic: (
          parseFloat(data.firstFuelTotalCostTermic?.toString() || "") * 100
        ).toFixed(0),
      }),
      ...((engineType === "all" || engineType === "electric") &&
        data.firstFuelCardElectricRecharge !== undefined && {
          firstFuelCardElectricRecharge: data.firstFuelCardElectricRecharge,
        }),
      ...((engineType === "all" || engineType === "electric") &&
        data.currentElectricTankFilling !== undefined && {
          firstFuelElectricKwh: parseFloat(
            data.currentElectricTankFilling?.toString()
          ).toFixed(0),
        }),
      ...((engineType === "all" || engineType === "electric") &&
        data.currentElectricTankFilling !== undefined && {
          currentElectricTankFilling: parseFloat(
            data.currentElectricTankFilling?.toString()
          ).toFixed(0),
        }),
    };

    const formData = new FormData();
    if (engineType !== "electric") {
      formData.append("file", uploadedFiles[0]);
    }
    formData.append(
      "request",
      new Blob([JSON.stringify(dataObject)], { type: "application/json" })
    );

    setLoading(true);
    VehicleEntranceService.firstFueling.post
      .doFirstFueling(tenant, formData)
      .then(
        (response) => {
          toggleConfirmStatus();
          setLoading(false);
          snackbar.show(
            "success",
            t(
              "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.firstFuelingDoneSuccess"
            )
          );
        },
        () => {
          snackbar.show(
            "error",
            t(
              "vehicleEntrance.vehicleEntranceFirstFueling.snackbar.firstFuelingDoneError"
            )
          );
          setLoading(false);
        }
      );
  };

  const onChangeNumber = (
    name: keyof IVehicleFirstFueling,
    value: string,
    decimalPointLength?: number,
    numberLength?: number,
    maxNumber?: number,
    minNumber?: number
  ) => {
    const newVal = onChangeTextNumber(value, {
      decimalPointLength,
      numberLength,
      maxNumber,
      minNumber,
    });
    if (newVal !== undefined) {
      form.setValue(name, newVal);
    }
  };

  const validateNumber = (val: string) => {
    if (val) {
      let isnum = /^[0-9]*[.,]?[0-9]*$/.test(val);

      if (!isnum) {
        return t(
          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.onlyNumber"
        );
      }
    }
  };

  const engineType = useMemo(() => {
    const electric =
      vehicleData?.enginePowerCode === "E" ||
      vehicleData?.enginePowerCode === "HB" ||
      vehicleData?.enginePowerCode === "HD";

    const termic =
      vehicleData?.enginePowerCode === "D" ||
      vehicleData?.enginePowerCode === "G" ||
      vehicleData?.enginePowerCode === "B" ||
      vehicleData?.enginePowerCode === "HB" ||
      vehicleData?.enginePowerCode === "HD";

    if (electric && termic) {
      return "all";
    } else if (electric) {
      return "electric";
    } else if (termic) {
      return "termic";
    }

    return "na";
  }, [vehicleData]);

  const msgLocationError = useMemo(() => {
    const locationId = form.watch("firstFuelLocationId");
    if (
      vehicleData &&
      userLocations &&
      userLocations.length > 0 &&
      (!locationId || !userLocations.find((loc) => loc.value === locationId))
    ) {
      if (!openErrorLocation) {
        toggleErrorLocation();
      }
      return t(
        "vehicleEntrance.vehicleEntranceFirstFueling.msgLocationError"
      ).replace("#textElement", globalData.user.username);
    }

    return null;
  }, [form.watch("firstFuelLocationId"), userLocations, vehicleData]);

  const locations = useMemo<AutoCompleteOption[]>(() => {
    return (
      [
        {
          label: `${vehicleData?.physicalLocationDescription} (${locationType(vehicleData?.physicalLocationType || "")})`,
          value: vehicleData?.physicalLocationId || ""
        }
      ]
    );
  }, [vehicleData]);

  const optionsTermicTankLevel: AutoCompleteOption[] = [
    {
      label: "0/8",
      value: 0,
    },
    {
      label: "1/8",
      value: 1,
    },
    {
      label: "2/8",
      value: 2,
    },
    {
      label: "3/8",
      value: 3,
    },
    {
      label: "4/8",
      value: 4,
    },
    {
      label: "5/8",
      value: 5,
    },
    {
      label: "6/8",
      value: 6,
    },
    {
      label: "7/8",
      value: 7,
    },
    {
      label: "8/8",
      value: 8,
    },
  ];

  return (
    <>
      <Mui.Box height="auto" padding={{ md: 2, xs: 0 }}>
        <Mui.Card style={{ height: "100%" }}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Mui.CardContent style={{ height: "100%" }}>
              <Mui.Stack alignItems="center" mb={2} spacing={2}>
                <Mui.Skeleton
                  variant="rectangular"
                  width="150px"
                  height="150px"
                />

                <Mui.Typography variant="h4">
                  {`${vehicleData?.model.brand.descr} ${vehicleData?.model.descr}`}
                </Mui.Typography>
              </Mui.Stack>

              <Mui.Grid container spacing={1}>
                <Mui.Grid item>
                  <Mui.Typography>
                    {t("vehicleEntrance.vehicleEntranceFirstFueling.targa")}
                  </Mui.Typography>
                </Mui.Grid>
                <Mui.Grid item>
                  <Mui.Typography fontWeight="bold">{plate}</Mui.Typography>
                </Mui.Grid>
              </Mui.Grid>

              <Mui.Grid container spacing={3}>
                <Mui.Grid item xs={12} md={4}>
                  <FormInput
                    name={nameof<IVehicleFirstFueling>("firstFuelLocationId")}
                    type="select"
                    options={locations}
                    label={t(
                      "vehicleEntrance.vehicleEntranceFirstFueling.locationFirstFueling"
                    )}
                    fullWidth
                    formInstance={form}
                    validation={{
                      required: t(
                        "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                      ),
                    }}
                    disabled={true}
                  />
                </Mui.Grid>
                <Mui.Grid item xs={12} md={4}>
                  <FormInput
                    name={nameof<IVehicleFirstFueling>("firstFuelDatetime")}
                    type="date"
                    label={t(
                      "vehicleEntrance.vehicleEntranceFirstFueling.dateFirstFueling"
                    )}
                    fullWidth
                    formInstance={form}
                    validation={{
                      required: t(
                        "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                      ),
                    }}
                    maxDateTime={new Date()}
                  />
                </Mui.Grid>

                {(engineType === "all" || engineType === "termic") && (
                  <>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "firstFuelTermicLiters"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputLitri.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputLitri.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        value={form.watch("firstFuelTermicLiters")}
                        onChange={(value) =>
                          onChangeNumber("firstFuelTermicLiters", value, 2)
                        }
                        validation={{
                          validate: validateNumber,
                        }}
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "firstFuelTermicCostPerLiter"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrency.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrency.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        value={form.watch("firstFuelTermicCostPerLiter")}
                        onChange={(value) =>
                          onChangeNumber(
                            "firstFuelTermicCostPerLiter",
                            value,
                            2
                          )
                        }
                        validation={{
                          validate: validateNumber,
                        }}
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "currentTermicTankFilling"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrentTermicTank.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrentTermicTank.placeholder"
                        )}
                        type="select"
                        options={optionsTermicTankLevel}
                        fullWidth
                        formInstance={form}
                        validation={{
                          required: t(
                            "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                          ),
                        }}
                        sx={{
                          input: {
                            width:
                              (form
                                .watch("currentTermicTankFilling")
                                ?.toString().length || 0) * 10,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <Mui.InputAdornment position="start">
                              {form.watch("currentTermicTankFilling") && "/8"}
                            </Mui.InputAdornment>
                          ),
                        }}
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "firstFuelTotalCostTermic"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputTotalCostTermic.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputTotalCostTermic.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        value={form.watch("firstFuelTotalCostTermic")}
                        onChange={(value) =>
                          onChangeNumber("firstFuelTotalCostTermic", value, 2)
                        }
                        validation={{
                          required: t(
                            "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                          ),
                          validate: validateNumber,
                        }}
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "firstFuelCardNumber"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCard.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCard.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        validation={{
                          required: t(
                            "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                          ),
                        }}
                      />
                    </Mui.Grid>
                  </>
                )}

                {(engineType === "all" || engineType === "electric") && (
                  <>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "currentElectricTankFilling"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrentElectricTank.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputCurrentElectricTank.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        value={form.watch("currentElectricTankFilling")}
                        onChange={(value) =>
                          onChangeNumber(
                            "currentElectricTankFilling",
                            value,
                            0,
                            3,
                            100,
                            0
                          )
                        }
                        validation={{
                          required: t(
                            "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                          ),
                          validate: (val) => {
                            const value = parseInt(val);

                            if (!/^[0-9]*$/.test(val)) {
                              return t(
                                "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.onlyNumber"
                              );
                            }

                            if (value < 0 || value > 100) {
                              return t(
                                "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.electricTankValue"
                              );
                            }
                          },
                        }}
                        sx={{
                          input: {
                            width:
                              (form
                                .watch("currentElectricTankFilling")
                                ?.toString().length || 0) * 10,
                          },
                        }}
                        InputProps={{
                          endAdornment: (
                            <Mui.InputAdornment position="start">
                              {form.watch("currentElectricTankFilling") && "%"}
                            </Mui.InputAdornment>
                          ),
                        }}
                      />
                    </Mui.Grid>
                    <Mui.Grid item xs={12} sm={6} md={4}>
                      <FormInput
                        name={nameof<IVehicleFirstFueling>(
                          "firstFuelCardElectricRecharge"
                        )}
                        label={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputElectricCard.label"
                        )}
                        placeholder={t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputElectricCard.placeholder"
                        )}
                        type="text"
                        fullWidth
                        formInstance={form}
                        validation={{
                          required: t(
                            "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                          ),
                        }}
                      />
                    </Mui.Grid>
                  </>
                )}

                {engineType !== "electric" && (
                  <Mui.Grid item xs={12} sm={6} md={4}>
                    <Mui.TextField
                      label={t(
                        "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputFileUploader.label"
                      )}
                      placeholder={t(
                        "vehicleEntrance.vehicleEntranceFirstFueling.inputs.inputFileUploader.placeholder"
                      )}
                      fullWidth
                      value={uploadedFiles?.[0]?.name || ""}
                      InputProps={{
                        endAdornment: (
                          <Mui.Button
                            variant="text"
                            size="icon"
                            onClick={toggleNewAggiungi}
                          >
                            <MuiIcons.AttachFile />
                          </Mui.Button>
                        ),
                      }}
                      error={
                        !!form.formState.submitCount &&
                        uploadedFiles.length === 0
                      }
                      helperText={
                        !!form.formState.submitCount &&
                        t(
                          "vehicleEntrance.vehicleEntranceFirstFueling.inputs.validations.required"
                        )
                      }
                    />
                  </Mui.Grid>
                )}
              </Mui.Grid>
            </Mui.CardContent>

            <Mui.CardActions>
              <Mui.Button
                variant="contained"
                type="submit"
                size="smallPlus"
                fullWidth
                disabled={msgLocationError ? true : false}
              >
                {t("vehicleEntrance.vehicleEntranceFirstFueling.button")}
              </Mui.Button>
            </Mui.CardActions>
          </form>
        </Mui.Card>
      </Mui.Box>

      <Dialogs.AggiungiAllegato
        open={openNewAggiungi}
        onClose={toggleNewAggiungi}
        onUploadFile={(file) => setUploadedFiles([file])}
      />

      <Dialogs.ChangeStatus
        open={openConfirmStatus}
        onClose={toggleConfirmStatus}
        vehicleId={vehicleData?.id || 0}
      />

      <Mui.Dialog
        open={openErrorLocation}
        onClose={toggleErrorLocation}
        fullWidth
        maxWidth="sm"
      >
        <Mui.DialogTitle>
          <Mui.Grid container spacing={1} alignItems={"center"}>
            <Mui.Grid item>
              <Mui.Stack direction="row" alignItems="center">
                <MuiIcons.WarningRounded color="warning" />
                <Mui.Typography variant="h6" ml={1}>
                  {t("vehicleEntrance.vehicleEntranceFirstFueling.warningMsg")}
                </Mui.Typography>
              </Mui.Stack>
            </Mui.Grid>
            <Mui.Grid item marginLeft={"auto"}>
              <Mui.IconButton onClick={toggleErrorLocation}>
                <MuiIcons.Close />
              </Mui.IconButton>
            </Mui.Grid>
          </Mui.Grid>
        </Mui.DialogTitle>

        <Mui.DialogContent>{msgLocationError}</Mui.DialogContent>

        <Mui.DialogActions>
          <Mui.Button onClick={toggleErrorLocation}>
            {t("actionButtons.close")}
          </Mui.Button>
        </Mui.DialogActions>
      </Mui.Dialog>
    </>
  );
};
export default VehicleEntranceFirstFueling;
